import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Banner, Spinner } from './States';
import { useCurrency } from '../hooks/useCurrency';
import { cleanName } from '../lib/format';
import type { CorDaPaleta, KitAdminItem, SkinGroup } from '../api/types';

const MAX = 60;

/** Uma peça já escolhida na montagem, antes de o kit existir. */
interface Escolha {
  baseKey: string;
  name: string;
  iconUrl: string | null;
  minPrice: number | null;
  /** Desgastes à venda, para fixar um. */
  variantes: SkinGroup['variants'];
  /** Código do desgaste escolhido. Vazio = o mais barato do momento. */
  exterior: string;
}

const daGrupo = (g: SkinGroup): Escolha => ({
  baseKey: g.key,
  name: g.name,
  iconUrl: g.iconUrl,
  minPrice: g.minPrice,
  variantes: g.variants ?? [],
  // Já entra com o mais barato marcado: é o que o kit mostraria de qualquer
  // jeito, e deixa explícito que dá para trocar.
  exterior: (g.variants ?? []).find((v) => v.price === g.minPrice)?.exterior?.code ?? '',
});

/** O preço do desgaste escolhido — ou o mais barato, quando não há escolha. */
function precoDaEscolha(e: Escolha): number | null {
  if (!e.exterior) return e.minPrice;
  return e.variantes.find((v) => v.exterior?.code === e.exterior)?.price ?? null;
}

/**
 * Busca de skins restrita a um tipo.
 *
 * Faca e luva têm slots separados de propósito: uma busca só devolveria as
 * duas misturadas e deixaria montar "kit" de duas facas. O `kind` vai para a
 * API, então o resultado já chega certo em vez de ser filtrado na tela.
 */
function BuscaPeca({
  kind,
  rotulo,
  exemplo,
  escolhida,
  onEscolher,
  desabilitado,
}: {
  kind: 'knife' | 'gloves';
  rotulo: string;
  exemplo: string;
  escolhida: Escolha | null;
  onEscolher: (e: Escolha | null) => void;
  desabilitado: boolean;
}) {
  const { money } = useCurrency();
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<SkinGroup[] | null>(null);
  const [buscando, setBuscando] = useState(false);

  // Espera a digitação parar: uma consulta por tecla varreria o catálogo
  // inteiro a cada letra.
  useEffect(() => {
    const termo = busca.trim();
    if (termo.length < 3) {
      setResultados(null);
      return;
    }
    setBuscando(true);
    const timer = window.setTimeout(() => {
      void api
        .skins({ kind, search: termo, pageSize: 18, sort: 'onsale' })
        .then((r) => setResultados(r.items))
        .catch(() => setResultados([]))
        .finally(() => setBuscando(false));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [busca, kind]);

  if (escolhida) {
    return (
      <div className="kit-slot escolhido">
        <span className="kit-slot-rotulo">{rotulo}</span>
        {escolhida.iconUrl ? <img src={escolhida.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
        <div className="info">
          <b>{cleanName(escolhida.name)}</b>
          {/*
            Fixar o desgaste evita que o kit troque de peça sozinho: sem isso
            ele sempre aponta para a variante mais barata do momento, e num
            mercado que gira rápido o card passa a oferecer outra coisa pelo
            mesmo nome.
          */}
          <select
            className="select"
            value={escolhida.exterior}
            onChange={(e) => onEscolher({ ...escolhida, exterior: e.target.value })}
            disabled={desabilitado}
          >
            <option value="">Qualquer desgaste (o mais barato)</option>
            {escolhida.variantes
              .filter((v) => v.exterior && v.price !== null)
              .map((v) => (
                <option key={v.exterior!.code} value={v.exterior!.code}>
                  {v.exterior!.pt} · {money(v.price)}
                </option>
              ))}
          </select>
        </div>
        <button className="btn ghost small" onClick={() => onEscolher(null)} disabled={desabilitado}>
          Trocar
        </button>
      </div>
    );
  }

  return (
    <div className="kit-slot">
      <span className="kit-slot-rotulo">{rotulo}</span>
      <input
        className="input"
        placeholder={`Busque: ${exemplo}`}
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        disabled={desabilitado}
      />
      {buscando && <span className="dim">buscando…</span>}
      {resultados && resultados.length === 0 && !buscando && <span className="dim">nada encontrado</span>}
      {resultados && resultados.length > 0 && (
        <div className="busca-grid compacta">
          {resultados.map((g) => (
            <button key={g.key} className="busca-card" onClick={() => onEscolher(daGrupo(g))} disabled={desabilitado}>
              {g.iconUrl ? <img src={g.iconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
              <b>{cleanName(g.name)}</b>
              <span className="dim">{money(g.minPrice)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * As cores do kit, como amostras para marcar.
 *
 * A cor é quem monta o kit que decide: é por ela que o cliente filtra a vitrine.
 * Pode marcar mais de uma — "azul e preto" é o jeito normal de descrever um par.
 *
 * Só as amostras, sem o nome em cada uma: onze etiquetas lado a lado viravam
 * uma faixa de texto maior que o próprio kit. O nome aparece ao passar o mouse
 * e, das marcadas, ao lado das amostras.
 */
function EscolherCores({
  paleta,
  marcadas,
  onMudar,
  desabilitado,
}: {
  paleta: CorDaPaleta[];
  marcadas: string[];
  onMudar: (cores: string[]) => void;
  desabilitado: boolean;
}) {
  const nomes = paleta.filter((c) => marcadas.includes(c.key)).map((c) => c.pt);
  return (
    <div className="kit-amostras">
      <div className="kit-amostras-grade" role="group" aria-label="Cores do kit">
        {paleta.map((c) => {
          const ativa = marcadas.includes(c.key);
          return (
            <button
              key={c.key}
              type="button"
              className={`kit-amostra${ativa ? ' ativa' : ''}`}
              style={{ background: c.hex }}
              aria-pressed={ativa}
              aria-label={c.pt}
              title={c.pt}
              disabled={desabilitado}
              onClick={() =>
                onMudar(
                  // Na ordem da paleta, para o kit ler igual no painel e na vitrine.
                  paleta.map((p) => p.key).filter((k) => (k === c.key ? !ativa : marcadas.includes(k))),
                )
              }
            >
              {ativa && (
                <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden>
                  <path
                    d="M3.5 8.5l3 3 6-7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          );
        })}
      </div>
      <span className="kit-amostras-nomes">{nomes.length > 0 ? nomes.join(' · ') : 'Nenhuma cor marcada'}</span>
    </div>
  );
}

/** As cores de um kit já salvo, só para leitura na linha da lista. */
function ResumoCores({ paleta, marcadas }: { paleta: CorDaPaleta[]; marcadas: string[] }) {
  const cores = paleta.filter((c) => marcadas.includes(c.key));
  if (cores.length === 0) {
    return <span className="kit-resumo-cores sem-cor">Sem cor · só aparece em “Todas” na vitrine</span>;
  }
  return (
    <span className="kit-resumo-cores">
      {cores.map((c) => (
        <span key={c.key} className="kit-resumo-cor">
          <span className="kit-cor-bola" style={{ background: c.hex }} />
          {c.pt}
        </span>
      ))}
    </span>
  );
}

/**
 * Montagem dos kits prontos.
 *
 * Um kit é uma faca e uma luva que combinam, com um desconto que só existe
 * levando as duas. O preço nunca é digitado: sai da soma do catálogo no
 * momento, menos o percentual. Assim o "economize R$ X" continua verdadeiro
 * quando o mercado mexe, sem ninguém revisar kit por kit.
 */
export function KitsPanel() {
  const { money } = useCurrency();
  const [items, setItems] = useState<KitAdminItem[] | null>(null);
  const [paleta, setPaleta] = useState<CorDaPaleta[]>([]);
  /**
   * Por que cada kit ficou de fora da vitrine.
   *
   * A linha dizia "peça indisponível no catálogo" para qualquer motivo, e o
   * motivo mais comum não é esse: é a cotação de kit não ficar abaixo da faixa
   * daquelas peças. Dizer a causa errada manda o admin procurar no lugar
   * errado — ele vai atrás do catálogo quando o problema é a tabela de preço.
   */
  const [motivos, setMotivos] = useState<Record<number, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // A montagem em curso, antes de virar kit.
  const [faca, setFaca] = useState<Escolha | null>(null);
  const [luva, setLuva] = useState<Escolha | null>(null);
  const [nome, setNome] = useState('');
  const [recado, setRecado] = useState('');
  const [cores, setCores] = useState<string[]>([]);
  /** O kit da lista com as cores abertas para edição. Um por vez. */
  const [editandoCores, setEditandoCores] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    try {
      const res = await api.adminKits();
      setItems(res.items);
      setPaleta(res.palette);
      setMotivos(Object.fromEntries(res.indisponiveis.map((i) => [i.id, i.motivo])));
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os kits.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const somaPrevia = (faca ? (precoDaEscolha(faca) ?? 0) : 0) + (luva ? (precoDaEscolha(luva) ?? 0) : 0);

  function limparMontagem() {
    setFaca(null);
    setLuva(null);
    setNome('');
    setRecado('');
    setCores([]);
  }

  async function criar() {
    if (!faca || !luva) return;
    setOcupado(true);
    setErro(null);
    setOk(null);
    try {
      await api.adminCreateKit({
        name: nome.trim(),
        knifeBaseKey: faca.baseKey,
        knifeName: faca.name,
        knifeExterior: faca.exterior || null,
        glovesBaseKey: luva.baseKey,
        glovesName: luva.name,
        glovesExterior: luva.exterior || null,
        note: recado.trim() || null,
        colors: cores,
      });
      setOk(`Kit "${nome.trim()}" montado.`);
      limparMontagem();
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui criar o kit.');
    } finally {
      setOcupado(false);
    }
  }

  async function salvar(id: number, patch: { name?: string; active?: boolean; colors?: string[] }) {
    setOcupado(true);
    setErro(null);
    try {
      await api.adminUpdateKit(id, patch);
      // Relê em vez de confiar na resposta: mudar o kit pode mudar o motivo
      // de ele estar fora da vitrine, e a resposta do PUT não traz isso.
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui salvar o kit.');
      await carregar();
    } finally {
      setOcupado(false);
    }
  }

  async function remover(id: number) {
    setOcupado(true);
    setErro(null);
    try {
      await api.adminRemoveKit(id);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui remover o kit.');
    } finally {
      setOcupado(false);
    }
  }

  async function mover(indice: number, direcao: -1 | 1) {
    if (!items) return;
    const destino = indice + direcao;
    if (destino < 0 || destino >= items.length) return;

    const nova = [...items];
    [nova[indice], nova[destino]] = [nova[destino]!, nova[indice]!];
    setItems(nova); // otimista: a lista responde na hora
    try {
      await api.adminReorderKits(nova.map((k) => k.id));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui reordenar.');
      await carregar();
    }
  }

  if (erro && !items) return <Banner kind="error">{erro}</Banner>;
  if (!items) return <Spinner label="Carregando os kits…" />;

  const indisponiveis = items.filter((k) => k.indisponivel).length;
  const inativos = items.filter((k) => !k.active).length;

  return (
    <div className="panel">
      <h2>Kits prontos</h2>
      <p className="muted">
        Uma faca e uma luva que combinam. O desconto não é definido aqui: as duas peças são precificadas pela{' '}
        <b>cotação de kit</b>, em Comissionamento, que é menor que a cotação normal. Por isso o percentual varia de um
        kit para outro — depende da faixa de valor em que cada peça cai.
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}
      {indisponiveis > 0 && (
        <Banner kind="warn">
          {indisponiveis === 1 ? '1 kit não está' : `${indisponiveis} kits não estão`} aparecendo para o cliente. O
          motivo de cada um está na linha dele — se for sobre cotação, o ajuste é na aba <b>Comissionamento</b>: o
          acréscimo de kit precisa ficar <i>abaixo</i> do acréscimo da faixa em que aquelas peças caem, senão o kit
          sairia mais caro que comprar separado.
        </Banner>
      )}

      <div className="stat-row">
        <span>
          <b>{items.length}</b> de {MAX} kits
        </span>
        {inativos > 0 && <span className="dim">{inativos} desligado(s)</span>}
      </div>

      {items.length === 0 ? (
        <p className="muted">Nenhum kit ainda. Monte o primeiro abaixo.</p>
      ) : (
        <ol className="featured-list kits-list">
          {items.map((k, i) => (
            <li key={k.id} className={k.indisponivel || !k.active ? 'indisponivel' : ''}>
              <span className="pos">{i + 1}</span>

              <div className="kit-pecas">
                {k.knifeIconUrl ? <img src={k.knifeIconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
                <span className="kit-mais">+</span>
                {k.glovesIconUrl ? <img src={k.glovesIconUrl} alt="" loading="lazy" /> : <span className="sem-img" />}
              </div>

              <div className="info">
                <b>{k.name}</b>
                <span className="dim">
                  {cleanName(k.knifeName)}
                  {k.knifeExteriorLabel ? ` (${k.knifeExteriorLabel})` : ''} + {cleanName(k.glovesName)}
                  {k.glovesExteriorLabel ? ` (${k.glovesExteriorLabel})` : ''}
                </span>
                {/* Kit sem cor não aparece em nenhum filtro da vitrine, só em "Todas". */}
                {editandoCores === k.id ? (
                  <div className="kit-cores-edicao">
                    <EscolherCores
                      paleta={paleta}
                      marcadas={k.colors}
                      onMudar={(novas) => void salvar(k.id, { colors: novas })}
                      desabilitado={ocupado}
                    />
                    <button className="btn ghost small" onClick={() => setEditandoCores(null)}>
                      Concluir
                    </button>
                  </div>
                ) : (
                  <ResumoCores paleta={paleta} marcadas={k.colors} />
                )}
                <span className="dim">
                  {k.indisponivel ? (
                    (motivos[k.id] ?? 'fora da vitrine por um motivo que não consegui identificar')
                  ) : (
                    <>
                      {money(k.somaCny)} → <b>{money(k.precoCny)}</b> · economia {money(k.economiaCny)}
                    </>
                  )}
                </span>
              </div>

              {/* O desconto não é digitado por kit: sai da cotação de kit,
                  lá no Comissionamento. Aqui ele é leitura — e varia de um kit
                  para outro porque a faixa de valor de cada peça é diferente. */}
              <span className="kit-desconto">{k.descontoPct === null ? '—' : `−${k.descontoPct.toFixed(1)}%`}</span>

              <div className="actions">
                <button className="btn ghost small" onClick={() => void mover(i, -1)} disabled={i === 0 || ocupado}>
                  ↑
                </button>
                <button
                  className="btn ghost small"
                  onClick={() => void mover(i, 1)}
                  disabled={i === items.length - 1 || ocupado}
                >
                  ↓
                </button>
                <button
                  className="btn ghost small"
                  onClick={() => setEditandoCores(editandoCores === k.id ? null : k.id)}
                  disabled={ocupado}
                  aria-expanded={editandoCores === k.id}
                >
                  Cores
                </button>
                <button
                  className="btn ghost small"
                  onClick={() => void salvar(k.id, { active: !k.active })}
                  disabled={ocupado}
                  title={k.active ? 'Tirar da vitrine sem apagar' : 'Voltar para a vitrine'}
                >
                  {k.active ? 'Desligar' : 'Ligar'}
                </button>
                <button className="btn ghost small" onClick={() => void remover(k.id)} disabled={ocupado}>
                  Remover
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      <h3>Montar um kit</h3>
      <div className="kit-montagem">
        <BuscaPeca
          kind="knife"
          rotulo="Faca"
          exemplo="Karambit, Butterfly, Bayonet…"
          escolhida={faca}
          onEscolher={setFaca}
          desabilitado={ocupado}
        />
        <BuscaPeca
          kind="gloves"
          rotulo="Luva"
          exemplo="Sport Gloves, Specialist, Driver…"
          escolhida={luva}
          onEscolher={setLuva}
          desabilitado={ocupado}
        />
      </div>

      <div className="kit-dados">
        <label className="field">
          <span>Nome do kit</span>
          <input
            className="input"
            placeholder="Fade Combo, Vice City…"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            disabled={ocupado}
          />
        </label>
        <label className="field">
          <span>Recado (opcional)</span>
          <input
            className="input"
            placeholder="combina com inventário roxo"
            value={recado}
            onChange={(e) => setRecado(e.target.value)}
            disabled={ocupado}
          />
        </label>
      </div>

      <div className="field">
        <span>Cores do kit</span>
        <EscolherCores paleta={paleta} marcadas={cores} onMudar={setCores} desabilitado={ocupado} />
        <small className="dim">É por elas que o cliente filtra a aba Kits. Marque as que descrevem o par.</small>
      </div>

      {/* A conta aparece antes de salvar: é o número que decide se o desconto
          está no ponto, e descobrir isso só depois de criar dá retrabalho. */}
      {faca && luva && (
        <div className="total-box kit-previa">
          <span>
            {cleanName(faca.name)} + {cleanName(luva.name)}
          </span>
          <b>{money(somaPrevia)}</b>
          <small>
            soma das duas pelo preço normal · o preço de kit sai da cotação de kit e aparece assim que você criar
          </small>
        </div>
      )}

      <div className="actions-row">
        <button
          className="btn primary"
          onClick={() => void criar()}
          disabled={ocupado || !faca || !luva || !nome.trim() || items.length >= MAX}
        >
          Criar kit
        </button>
        <button className="btn ghost" onClick={limparMontagem} disabled={ocupado || (!faca && !luva && !nome)}>
          Limpar
        </button>
      </div>
    </div>
  );
}
